import type { IncomingLearningRecord } from "@bindery/convex-reader/submission";
import { randomUUID } from "@bindery/logging/random-uuid";
import {
  appendResponse,
  type ResponseId,
} from "@bindery/shared/contracts/fluency";
import { extractNormalizedWord } from "@bindery/shared/contracts/word";
import {
  readMeta,
  writeMeta,
  type FluencyRow,
  type ReaderDb,
} from "@/storage/db";
import { READER_MISMATCH, type SyncBackend } from "./backend";
import { withServerHistory } from "./fluency-history";
import {
  readSubmitEpoch,
  type FluencyResponseInput,
  type Outbox,
} from "./outbox";
import { preQueueResponse } from "./pre-queue";
import { systemClock, type Clock, type SyncLogger } from "./retry";

export const CHANGED_SINCE_PAGE_SIZE = 256;
// fluency.forWords refuses more (MAX_WORDS_PER_LOOKUP in convex-reader).
const FOR_WORDS_BATCH = 500;
// Each retry first waits out the running flush; past this the answer is
// dropped and the next trigger fetches again.
const STALE_FETCH_RETRIES = 3;

export const fluencyWatermarkKey = (readerId: string): string =>
  `fluencyWatermark:${readerId}`;

export interface FluencyStoreDeps {
  db: ReaderDb;
  backend: SyncBackend;
  outbox: Pick<
    Outbox,
    "queueFluencyResponses" | "queueRecord" | "holdTapRecord" | "whenIdle"
  >;
  /** The session's reader: the only one fetched for. */
  currentReaderId: () => string | null;
  /** Whose histories are held and appended locally; defaults to the session's. */
  localReaderId?: () => string | null;
  logger: SyncLogger;
  clock?: Clock;
  /** The token named another reader than the store's: re-read the session. */
  onReaderMismatch?: () => void;
}

/** A tap's record, queued in the same transaction as its responses. */
export interface TapRecord {
  record: IncomingLearningRecord;
  held: boolean;
}

export interface FluencyStore {
  /** The word's history as the next tap should see it; synchronous. */
  get: (word: string) => ResponseId[];
  /**
   * The server's history for the word has not reached this tab yet; this
   * tab's own appends, however many, do not warm it.
   */
  isCold: (word: string) => boolean;
  /**
   * Applies in memory now and persists through the outbox, unawaited. A
   * `readerId` no longer the session's is persisted under it and kept out
   * of memory, which holds only the current reader's histories.
   */
  append: (
    responses: readonly FluencyResponseInput[],
    tap?: TapRecord,
    readerId?: string,
  ) => Promise<void>;
  /** Stored rows first, then `fluency.forWords`, for an opening reading. */
  hydrate: (words: readonly string[]) => Promise<void>;
  /** Replaces the words' stored histories with the server's, pending replayed. */
  refreshWords: (readerId: string, words: readonly string[]) => Promise<void>;
  /** One-shot paginated `fluency.changedSince` from the stored watermark. */
  syncChangedSince: () => Promise<void>;
}

export function createFluencyStore({
  db,
  backend,
  outbox,
  currentReaderId,
  localReaderId = currentReaderId,
  logger,
  clock = systemClock,
  onReaderMismatch,
}: FluencyStoreDeps): FluencyStore {
  let memoryReader: string | null = null;
  const memory = new Map<string, ResponseId[]>();
  const serverMerged = new Set<string>();
  // A stored or fetched history must not overwrite a word whose in-memory
  // append has not reached Dexie yet, or the next tap would lose it.
  const unsettled = new Map<string, number>();
  let syncing: Promise<void> | null = null;
  let syncAgain = false;

  function readerForMemory(): string | null {
    const readerId = localReaderId();
    if (readerId !== memoryReader) {
      memoryReader = readerId;
      memory.clear();
      serverMerged.clear();
      unsettled.clear();
    }
    return readerId;
  }

  function settle(readerId: string, row: FluencyRow) {
    if (memoryReader !== readerId) return;
    if (row.serverMerged) serverMerged.add(row.word);
    if ((unsettled.get(row.word) ?? 0) === 0) memory.set(row.word, row.history);
  }

  function changeUnsettled(words: readonly string[], delta: number): void {
    for (const word of words) {
      const next = (unsettled.get(word) ?? 0) + delta;
      if (next > 0) unsettled.set(word, next);
      else unsettled.delete(word);
    }
  }

  async function append(
    responses: readonly FluencyResponseInput[],
    tap?: TapRecord,
    pinned?: string,
  ): Promise<void> {
    const current = readerForMemory();
    const readerId = pinned ?? current;
    if (readerId === null) return;
    const inMemory = readerId === current;
    const now = clock.now();
    const withIds = responses.map((response) => ({
      ...response,
      clientResponseId: response.clientResponseId ?? randomUUID(),
    }));
    const words: string[] = [];
    for (const response of withIds) {
      const checked = preQueueResponse(response, readerId, now);
      if (checked.kind !== "queue") continue;
      const { word } = checked.value;
      if (!inMemory) continue;
      if (response.source === "tap" && !serverMerged.has(word)) {
        logger.event("sync.fluency.cold_tap", {
          word,
          reading_id: response.readingId,
        });
      }
      memory.set(
        word,
        appendResponse(memory.get(word) ?? [], response.response as ResponseId),
      );
      words.push(word);
    }
    changeUnsettled(words, 1);
    try {
      const rows = await (tap === undefined
        ? outbox.queueFluencyResponses(withIds, readerId)
        : tap.held
          ? outbox.holdTapRecord(tap.record, withIds, readerId)
          : outbox.queueRecord(tap.record, withIds, readerId));
      changeUnsettled(words, -1);
      for (const row of rows) settle(readerId, row);
    } catch (error) {
      changeUnsettled(words, -1);
      logger.error("sync.fluency.error", error, { stage: "append" });
    }
  }

  async function hydrate(words: readonly string[]): Promise<void> {
    const readerId = readerForMemory();
    if (readerId === null) return;
    const normalized = [...new Set(words.map(extractNormalizedWord))].filter(
      (word) => word !== "",
    );
    const stored = await db.fluency.bulkGet(
      normalized.map((word): [string, string] => [readerId, word]),
    );
    for (const row of stored) {
      if (row) settle(readerId, row);
    }
    if (currentReaderId() === readerId) {
      await fetchForWords(readerId, normalized);
    }
  }

  async function fetchForWords(
    readerId: string,
    normalized: readonly string[],
  ): Promise<void> {
    try {
      for (let start = 0; start < normalized.length; start += FOR_WORDS_BATCH) {
        const batch = normalized.slice(start, start + FOR_WORDS_BATCH);
        const fetched = await betweenSubmits(
          () => backend.forWords(readerId, batch),
          (histories, fetchedAtEpoch) =>
            histories === READER_MISMATCH ||
            histories === null ||
            currentReaderId() !== readerId
              ? Promise.resolve(true)
              : applyServerHistories(
                  readerId,
                  Object.entries(histories).map(([word, history]) => ({
                    word,
                    responseHistory: history,
                  })),
                  { persistEmpty: false },
                  fetchedAtEpoch,
                ),
        );
        if (fetched === null) return;
        if (fetched.answer === READER_MISMATCH) {
          onReaderMismatch?.();
          return;
        }
        if (fetched.answer === null || currentReaderId() !== readerId) return;
      }
    } catch (error) {
      logger.error("sync.fluency.error", error, { stage: "for_words" });
    }
  }

  /**
   * Fetches and applies an answer that no submit overlapped: the merge
   * goes ahead only if no drain, in any tab, started or ended since the
   * fetch began, so the answer holds every acknowledged response and none
   * still in the outbox. No lock is held over the network.
   */
  async function betweenSubmits<T>(
    fetch: () => Promise<T>,
    apply: (answer: T, fetchedAtEpoch: number) => Promise<boolean>,
  ): Promise<{ answer: T } | null> {
    for (let attempt = 0; attempt < STALE_FETCH_RETRIES; attempt += 1) {
      await outbox.whenIdle();
      const fetchedAtEpoch = await readSubmitEpoch(db);
      const answer = await fetch();
      if (await apply(answer, fetchedAtEpoch)) return { answer };
    }
    return null;
  }

  async function applyServerHistories(
    readerId: string,
    states: readonly {
      word: string;
      responseHistory: ResponseId[];
      lastUpdated?: number;
    }[],
    { persistEmpty }: { persistEmpty: boolean },
    fetchedAtEpoch: number,
  ): Promise<boolean> {
    const rows = await db.transaction(
      "rw",
      db.fluency,
      db.outboxResponses,
      db.meta,
      async () => {
        if ((await readSubmitEpoch(db)) !== fetchedAtEpoch) return null;
        const merged = [];
        for (const state of states) {
          const [existing, pending] = await Promise.all([
            db.fluency.get([readerId, state.word]),
            db.outboxResponses
              .where("[readerId+word]")
              .equals([readerId, state.word])
              .toArray(),
          ]);
          const row: FluencyRow = {
            ...withServerHistory(
              readerId,
              state.word,
              state.responseHistory,
              state.lastUpdated ?? existing?.lastUpdated ?? 0,
              pending,
            ),
            serverMerged: true,
          };
          const worthStoring =
            persistEmpty || existing !== undefined || row.history.length > 0;
          if (worthStoring) await db.fluency.put(row);
          merged.push(row);
        }
        return merged;
      },
    );
    if (rows === null) return false;
    for (const row of rows) settle(readerId, row);
    return true;
  }

  async function changedSinceOnce(): Promise<void> {
    const readerId = readerForMemory();
    if (readerId === null || readerId !== currentReaderId()) return;
    const key = fluencyWatermarkKey(readerId);
    const since = (await readMeta<number>(db, key)) ?? 0;
    let watermark = since;
    let cursor: string | null = null;
    try {
      for (;;) {
        const fetched = await betweenSubmits(
          () =>
            backend.changedSince(
              readerId,
              since,
              cursor,
              CHANGED_SINCE_PAGE_SIZE,
            ),
          (answer, fetchedAtEpoch) =>
            answer === READER_MISMATCH ||
            answer === null ||
            currentReaderId() !== readerId
              ? Promise.resolve(true)
              : applyServerHistories(
                  readerId,
                  answer.page,
                  { persistEmpty: true },
                  fetchedAtEpoch,
                ),
        );
        if (fetched === null) return;
        const page = fetched.answer;
        if (page === READER_MISMATCH) {
          onReaderMismatch?.();
          return;
        }
        if (page === null || currentReaderId() !== readerId) return;
        watermark = page.page.reduce(
          (max, state) => Math.max(max, state.lastUpdated),
          watermark,
        );
        await writeMeta(db, key, watermark);
        if (page.isDone) return;
        cursor = page.continueCursor;
      }
    } catch (error) {
      logger.error("sync.fluency.error", error, { stage: "changed_since" });
    }
  }

  return {
    get(word) {
      readerForMemory();
      return memory.get(extractNormalizedWord(word)) ?? [];
    },
    isCold(word) {
      readerForMemory();
      return !serverMerged.has(extractNormalizedWord(word));
    },
    append,
    hydrate,
    async refreshWords(readerId, words) {
      if (readerForMemory() !== readerId) return;
      await fetchForWords(readerId, words);
    },
    // A call during a running sync queues another rather than sharing it:
    // the running one may have fetched before the submit that prompted
    // this call.
    syncChangedSince() {
      if (syncing) {
        syncAgain = true;
        return syncing;
      }
      syncing = (async () => {
        try {
          do {
            syncAgain = false;
            await changedSinceOnce();
          } while (syncAgain);
        } finally {
          syncing = null;
        }
      })();
      return syncing;
    },
  };
}
